import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock, Linkedin, ArrowUpRight } from "lucide-react";
import api from "../api/client";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CursorDot from "../components/CursorDot";
import NewsletterCTA from "../components/NewsletterCTA";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/blogs/slug/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const shareUrl = `https://thelinkedinlab.in/blog/${slug}`;

  return (
    <main style={{ background: "#EBEBEB" }}>
      {post && (
        <Helmet>
          <title>{(post.seo_title || post.title) + " — LinkedIn Lab"}</title>
          <meta
            name="description"
            content={post.seo_description || post.excerpt}
          />
          <link rel="canonical" href={shareUrl} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={post.seo_title || post.title} />
          <meta
            property="og:description"
            content={post.seo_description || post.excerpt}
          />
          <meta property="og:url" content={shareUrl} />
          <meta
            property="og:image"
            content={post.cover_image || "https://thelinkedinlab.in/og-image.png"}
          />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={post.seo_title || post.title} />
          <meta
            name="twitter:description"
            content={post.seo_description || post.excerpt}
          />
          <meta
            name="twitter:image"
            content={post.cover_image || "https://thelinkedinlab.in/og-image.png"}
          />
        </Helmet>
      )}

      <CursorDot />
      <Navbar />

      <article className="pt-[160px] pb-20">
        <div className="container-x" style={{ maxWidth: 820 }}>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-[14px] text-neutral-600 hover:text-neutral-900 transition mb-8"
          >
            <ArrowLeft size={14} /> All posts
          </button>

          {loading && (
            <div className="py-20 text-center text-neutral-500">Loading...</div>
          )}

          {notFound && (
            <div className="py-20 text-center">
              <h1
                className="font-display"
                style={{ fontSize: 52, letterSpacing: "-0.03em" }}
              >
                Not found
              </h1>
              <p className="mt-4 text-neutral-600">
                This post doesn&apos;t exist or hasn&apos;t been published yet.
              </p>
              <Link
                to="/blog"
                className="mt-6 inline-flex items-center gap-2 text-[15px] font-medium"
                style={{ color: "var(--agero-orange)" }}
              >
                Back to all posts →
              </Link>
            </div>
          )}

          {post && (
            <>
              <div className="flex items-center gap-2 flex-wrap mb-5">
                {(post.tags || []).map((t) => (
                  <span
                    key={t}
                    className="px-2.5 py-1 rounded-full text-[11px] font-medium"
                    style={{ background: "rgba(255,51,1,0.10)", color: "var(--agero-orange)" }}
                  >
                    {t}
                  </span>
                ))}
              </div>

              <h1
                className="font-display"
                style={{
                  fontSize: "clamp(36px, 5vw, 64px)",
                  letterSpacing: "-0.045em",
                  lineHeight: 1.02,
                }}
              >
                {post.title}
              </h1>

              <div className="mt-6 flex items-center gap-3 text-sm text-neutral-600 flex-wrap">
                <span className="font-medium text-neutral-800">{post.author}</span>
                <span>&middot;</span>
                <span>{formatDate(post.published_at)}</span>
                <span>&middot;</span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={13} />
                  {post.reading_time} min read
                </span>
              </div>

              {post.substack_url && (
                <a
                  href={post.substack_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-medium transition group"
                  style={{
                    background: "rgba(255,51,1,0.10)",
                    color: "var(--agero-orange)",
                  }}
                >
                  <span>Also published on Substack</span>
                  <ArrowUpRight
                    size={14}
                    className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </a>
              )}

              {post.cover_image && (
                <div
                  className="mt-10 rounded-[24px] overflow-hidden"
                  style={{ aspectRatio: "16/9", background: "#111" }}
                >
                  <img
                    src={post.cover_image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="prose-blog mt-10">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {post.content}
                </ReactMarkdown>
              </div>

              <div className="mt-14 grid md:grid-cols-2 gap-5">
                <NewsletterCTA />
                <div
                  className="rounded-3xl p-7 md:p-8 flex flex-col justify-between"
                  style={{ background: "#0A0A0A", color: "#fff", minHeight: 240 }}
                >
                  <div>
                    <div
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wider uppercase mb-4"
                      style={{ background: "rgba(255,255,255,0.10)" }}
                    >
                      Work with us
                    </div>
                    <div
                      className="font-display"
                      style={{
                        fontSize: "clamp(22px, 2.4vw, 30px)",
                        letterSpacing: "-0.025em",
                        lineHeight: 1.15,
                      }}
                    >
                      Want LinkedIn to actually generate pipeline?
                    </div>
                    <p className="mt-3 text-white/75 text-[15px] leading-relaxed max-w-md">
                      Book a 30-minute discovery call. No pitch. We figure out
                      if there&apos;s a real fit.
                    </p>
                  </div>
                  <Link
                    to="/#booking"
                    className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#0A0A0A] font-medium hover:bg-neutral-200 transition self-start"
                  >
                    Book a Call
                  </Link>
                </div>
              </div>

              <div className="mt-10 flex items-center gap-3">
                <span className="text-sm text-neutral-500">Share:</span>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-9 h-9 rounded-full flex items-center justify-center bg-white border border-neutral-200 hover:bg-neutral-100 transition"
                  aria-label="Share on LinkedIn"
                >
                  <Linkedin size={15} />
                </a>
                <a
                  href={`https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-9 h-9 rounded-full flex items-center justify-center bg-white border border-neutral-200 hover:bg-neutral-100 transition text-[13px] font-bold"
                  aria-label="Share on X"
                >
                  X
                </a>
              </div>
            </>
          )}
        </div>
      </article>

      <Footer />
    </main>
  );
}
